<template>
  <div id="pos-payment-modal" class="pd">
    <div v-if="paymentFull && !loading">
      <div id="print-area" ref="printArea">
        <!-- Encabezado: monto y estados -->
        <div class="pd-header" :class="{ 'pd-header--refund': isRefund }">
          <div>
            <div class="pd-header__eyebrow">
              {{ isRefund ? "Web refund" : "Web payment" }} #{{
                paymentFull.id
              }}
            </div>
            <div
              class="pd-header__amount"
              :class="{ 'pd-header__amount--refund': isRefund }"
            >
              {{ money(paymentFull.amount) }}
            </div>
            <div v-if="paymentFull.orderNumber" class="pd-header__sub">
              Order
              <router-link
                :to="orderRoute(paymentFull.orderNumber)"
                target="_blank"
                class="pd-link"
                >#{{ paymentFull.orderNumber }}</router-link
              >
              <template v-if="customerName"> · {{ customerName }}</template>
            </div>
          </div>
          <div class="pd-header__side">
            <div class="pd-pills">
              <span v-if="isRefund" class="pd-pill pd-pill--red">↩ REFUND</span>
              <span
                v-else-if="paymentFull.statusSuccess == '0'"
                class="pd-pill pd-pill--red"
                >✕ DENIED</span
              >
              <span v-else class="pd-pill pd-pill--green">✓ APPROVED</span>
              <span v-if="refundStatus" class="pd-pill pd-pill--yellow">{{
                refundStatus
              }}</span>
              <span
                v-if="paymentFull.paymentType"
                class="pd-pill pd-pill--stripe"
                >{{ paymentFull.paymentType }}</span
              >
            </div>
            <div class="pd-header__date">
              {{ paymentFull.dateTransaction | dateWithHours }}
            </div>
          </div>
        </div>

        <PaymentRefundBanner
          :is-refund="isRefund"
          :amount="paymentFull.amount"
          :original-id="paymentFull.refundOf ? paymentFull.refundOf.id : null"
          :original-reference="
            paymentFull.refundOf ? paymentFull.refundOf.transactionId : null
          "
          :by="refundedBy"
          :date="paymentFull.dateTransaction | dateWithHours"
          :refunded-amount="Number(paymentFull.amountRefunded || 0)"
          :refunds="paymentFull.refunds || []"
          @openPayment="$emit('openPayment', $event)"
        />

        <div class="pd-grid">
          <PaymentDetailSection title="Payment">
            <PaymentDetailField
              :label="isRefund ? 'Amount refunded' : 'Amount paid'"
            >
              <span
                :class="
                  isRefund ? 'pd-refund-amount' : 'text-green-700 font-semibold'
                "
                >{{ money(paymentFull.amount) }}</span
              >
            </PaymentDetailField>
            <StripeLiveRefundStatus
              v-if="isStripePayment"
              :key="`live-${paymentFull.id}-${paymentFull.amountRefunded}`"
              :payment-intent-id="paymentFull.transactionId"
              :order-number="Number(paymentFull.orderNumber)"
            />
            <PaymentDetailField
              label="Processor"
              :value="paymentFull.paymentType"
            />
            <PaymentDetailField
              label="Cardholder"
              :value="paymentFull.cardholderName"
            />
            <PaymentDetailField
              label="Card last digits"
              :value="paymentFull.card"
              mono
            />
            <PaymentDetailField label="Zip code" :value="paymentFull.zip" />
            <PaymentDetailField
              v-if="paymentFull.paypalPayerEmail"
              label="Paypal email"
              :value="paymentFull.paypalPayerEmail"
            />
            <PaymentDetailField
              v-if="refundedBy"
              label="Refunded by"
              :value="refundedBy"
            />
          </PaymentDetailSection>

          <PaymentDetailSection title="Order">
            <PaymentDetailField label="Order number">
              <router-link
                v-if="paymentFull.orderNumber"
                :to="orderRoute(paymentFull.orderNumber)"
                target="_blank"
                class="pd-link"
                >{{ paymentFull.orderNumber }}</router-link
              >
              <span v-else class="text-gray-300">—</span>
            </PaymentDetailField>
            <PaymentDetailField
              label="Order total"
              strong
              :value="optionalMoney(paymentFull.order?.total)"
            />
            <PaymentDetailField
              label="Shipping"
              :value="optionalMoney(paymentFull.order?.shippingPaid)"
            />
            <PaymentDetailField
              label="Tax"
              :value="optionalMoney(paymentFull.order?.tax)"
            />
            <PaymentDetailField label="Source" value="web" />
            <PaymentDetailField
              label="Seller"
              :value="paymentFull.order?.seller?.name"
            />
          </PaymentDetailSection>

          <PaymentDetailSection title="Customer">
            <PaymentDetailField label="Name" :value="customerName" />
            <PaymentDetailField
              v-if="companyName"
              label="Company"
              :value="companyName"
            />
            <PaymentDetailField
              label="Email"
              :value="paymentFull.order?.customerEmail"
            />
          </PaymentDetailSection>

          <PaymentDetailSection title="Transaction">
            <PaymentDetailField
              label="Payment ID"
              :value="paymentFull.id"
              mono
            />
            <PaymentDetailField
              label="Transaction ID"
              :value="paymentFull.transactionId"
              mono
            />
            <PaymentDetailField
              v-if="paymentFull.transactionTag"
              label="Transaction tag"
              :value="paymentFull.transactionTag"
              mono
            />
            <PaymentDetailField
              v-if="paymentFull.paypalPayerId"
              label="Paypal payer ID"
              :value="paymentFull.paypalPayerId"
              mono
            />
            <PaymentDetailField label="Response">
              <details v-if="isLongText(paymentFull.response)" class="pd-raw">
                <summary>View response</summary>
                <pre>{{ prettyText(paymentFull.response) }}</pre>
              </details>
              <template v-else-if="paymentFull.response">{{
                paymentFull.response
              }}</template>
              <span v-else class="text-gray-300">—</span>
            </PaymentDetailField>
            <PaymentDetailField
              v-if="
                !refundedBy &&
                  paymentFull.moreInfo &&
                  paymentFull.moreInfo !== '0'
              "
              label="More info"
              :value="paymentFull.moreInfo"
            />
          </PaymentDetailSection>
        </div>

        <!-- Reembolsos registrados del pago -->
        <div
          v-if="paymentFull.refunds && paymentFull.refunds.length > 0"
          class="pd-table-card"
        >
          <div class="pd-table-card__title">
            <span>↩ Refunds</span>
            <span>{{ paymentFull.refunds.length }}</span>
          </div>
          <div class="pd-table-wrap">
            <table class="pd-table">
              <thead>
                <tr>
                  <th>Payment #</th>
                  <th>Date</th>
                  <th>Stripe refund</th>
                  <th class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="refund in paymentFull.refunds" :key="refund.id">
                  <td>
                    <button
                      class="pd-link"
                      @click="$emit('openPayment', String(refund.id))"
                    >
                      #{{ refund.id }}
                    </button>
                  </td>
                  <td>{{ refund.dateTransaction | dateWithHours }}</td>
                  <td class="pd-mono">{{ refund.transactionId }}</td>
                  <td class="text-right pd-refund-amount">
                    {{ money(refund.amount) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="pd-actions no-print">
        <button class="pd-btn" @click="print()">🖨 Print</button>
        <button
          v-if="paymentFull.statusSuccess != '0'"
          class="pd-btn pd-btn--blue"
          @click="showEmailModal = true"
        >
          ✉ {{ isRefund ? "Send refund receipt" : "Send receipt by email" }}
        </button>
        <button
          v-if="isStripePayment && canRefund"
          class="pd-btn pd-btn--stripe"
          @click="showStripeModal = true"
        >
          Stripe details / Refund
        </button>
      </div>
    </div>
    <div v-else-if="loading" class="pd-loading">
      <BaseSmallSpinner />
    </div>
    <div v-else class="pd-header">
      Error loading payment
    </div>
    <SendReceiptModal
      v-if="showEmailModal && paymentFull"
      :default-email="
        (paymentFull.order && paymentFull.order.customerEmail) || ''
      "
      :is-refund="isRefund"
      :send-fn="email => sendOrderPaymentReceipt(paymentFull.id, email)"
      @close="showEmailModal = false"
    />
    <StripePaymentDetailModal
      v-if="showStripeModal && paymentFull && canRefund"
      :show="showStripeModal"
      :order-number="Number(paymentFull.orderNumber)"
      :payment-intent-id="paymentFull.transactionId"
      @close="showStripeModal = false"
      @refunded="getPayment()"
    />
  </div>
</template>
<script>
import {
  getOrderPaymentApi as getPaymentApi,
  sendOrderPaymentReceipt
} from "@/api/store/PaymentsService.js";
import SendReceiptModal from "@/components/payments/SendReceiptModal.vue";
import StripePaymentDetailModal from "@/components/order/StripePaymentDetailModal.vue";
import StripeLiveRefundStatus from "@/components/payments/StripeLiveRefundStatus.vue";
import PaymentRefundBanner from "@/components/payments/PaymentRefundBanner.vue";
import PaymentDetailSection from "@/components/payments/PaymentDetailSection.vue";
import PaymentDetailField from "@/components/payments/PaymentDetailField.vue";
import paymentDetailHelpers from "@/components/payments/paymentDetailHelpers.js";
import printElement from "@/utils/print-element.js";
export default {
  name: "PaymentOrderModal",
  components: {
    StripePaymentDetailModal,
    StripeLiveRefundStatus,
    PaymentRefundBanner,
    PaymentDetailSection,
    PaymentDetailField,
    SendReceiptModal
  },
  mixins: [paymentDetailHelpers],
  props: {
    paymentId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      paymentFull: null,
      isOpenModal: this.value,
      loading: true,
      showStripeModal: false,
      showEmailModal: false
    };
  },
  computed: {
    customerName() {
      const order = this.paymentFull?.order;
      if (order?.customer) {
        return `${order.customer.generalInfo.name || ""} ${order.customer
          .generalInfo.surname || ""}`.trim();
      }
      if (order?.customerInfoauto) {
        return order.customerInfoauto.generalInfo.name;
      }
      return this.paymentFull?.cardholderName || null;
    },
    companyName() {
      return (
        this.paymentFull?.order?.customer?.generalInfo?.companyName || null
      );
    },
    isRefund() {
      return (
        !!this.paymentFull &&
        (Number(this.paymentFull.amount) < 0 || !!this.paymentFull.refundOfId)
      );
    },
    // more_info de un refund: "refund_of:pi_... | by: Nombre <email> (#id)"
    refundedBy() {
      const match = String(this.paymentFull?.moreInfo || "").match(
        /^refund_of:.*\| by: (.+)$/
      );
      return match ? match[1] : null;
    },
    // Estado de reembolso según amount_refunded del pago original
    refundStatus() {
      const refunded = Number(this.paymentFull?.amountRefunded || 0);
      if (refunded <= 0) return null;
      return refunded >= Number(this.paymentFull.amount) - 0.005
        ? "REFUNDED"
        : "PARTIALLY REFUNDED";
    },
    // Cobro Stripe (no la fila negativa de un refund)
    isStripePayment() {
      return (
        String(this.paymentFull?.transactionId || "").startsWith("pi_") &&
        Number(this.paymentFull?.amount) > 0
      );
    },
    canRefund() {
      return this.$store.getters["auth/userHasPermission"](
        "can_refund_payment"
      );
    }
  },
  mounted() {
    this.getPayment();
    window.addEventListener("keypress", this.closeKey);
  },
  watch: {
    paymentId(val) {
      if (val != "") {
        this.paymentFull = null;
        this.getPayment();
      }
    }
  },
  methods: {
    closeKey(event) {
      // No cerrar mientras se escribe (p. ej. la nota del refund) ni con el
      // modal de Stripe abierto.
      const target = event.target;
      if (
        this.showStripeModal ||
        this.showEmailModal ||
        (target &&
          (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
            target.isContentEditable))
      ) {
        return;
      }
      if (event.keyCode === 113) {
        this.$emit("closedPayment");
      }
    },
    async getPayment() {
      if (this.paymentId == "") {
        return;
      }
      this.loading = true;
      try {
        this.paymentFull = await getPaymentApi(this.paymentId);
      } catch (error) {
        console.log(error);
      }
      this.loading = false;
    },
    print() {
      printElement(this.$refs.printArea, {
        title: `Web ${this.isRefund ? "refund" : "payment"} #${
          this.paymentFull.id
        }`
      });
    },
    sendOrderPaymentReceipt(paymentId, email) {
      return sendOrderPaymentReceipt(paymentId, email);
    }
  },
  beforeDestroy() {
    window.removeEventListener("keypress", this.closeKey);
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/payment-detail.scss";
</style>
